"use client";

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/auth/use-auth";
import type { NotificationChannelType } from "@/lib/notifications/types";

export interface ChannelOption {
  id: string;
  type: NotificationChannelType;
  name: string;
  enabled: boolean;
  /** Platform-provided transport — selectable without connecting an inbox of your own. */
  isSystem: boolean;
  /** Enabled is not usable: telegram needs enrolled chats, email needs recipients. */
  deliverable: boolean;
}

export const notificationChannelOptionsQueryKey = ["notifications", "channel-options"] as const;

/**
 * The tenant's channels as a minimal projection (no config). Shared query key so
 * the handover picker and the billing channel-select share one cached fetch.
 */
export function useNotificationChannelOptions() {
  const { fetchWithAuth } = useAuth();

  const query = useQuery<ChannelOption[]>({
    queryKey: notificationChannelOptionsQueryKey,
    // Fresh on mount (matching the old useEffect): the channel CRUD lives in a
    // separate legacy hook that mutates its own state, not this cache.
    staleTime: 0,
    queryFn: async () => {
      const res = await fetchWithAuth("/api/notifications/channels/options");
      if (!res.ok) return [];
      const data = await res.json();
      return data.options ?? [];
    },
  });

  return { options: query.data ?? [], loading: query.isLoading };
}
